import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
import { storefrontHostSchema } from "./storefront.js";
/**
 * Ads and analytics (B-82, SF-14): the shop's Facebook Pixel with the Conversions API (server-side Purchase,
 * deduplicated with the browser's by the order ID) and Google Analytics 4. The CAPI access token is write-only.
 */
const tag = { tag: "storefront" };
export const pixelIdSchema = z
    .string()
    .trim()
    .regex(/^\d{10,20}$/);
export const ga4IdSchema = z
    .string()
    .trim()
    .regex(/^G-[A-Z0-9]{4,16}$/);
export const storefrontTrackingSchema = z.object({
    pixelId: z.string().nullable(),
    /** A Conversions API token is saved (it is never shown again). */
    capiConfigured: z.boolean(),
    /** Meta's "test events" code while checking the setup; empty when live. */
    testEventCode: z.string(),
    ga4Id: z.string().nullable(),
    /** 0 = never saved. */
    version: z.int(),
});
export const saveStorefrontTrackingSchema = z
    .object({
    pixelId: pixelIdSchema.nullable(),
    /** Omit to keep the saved token; null removes it. */
    capiToken: z.string().trim().min(20).max(400).nullable().optional(),
    testEventCode: z.string().trim().max(40).default(""),
    ga4Id: ga4IdSchema.nullable(),
    version: z.int().min(0),
})
    .strict();
export const getStorefrontTracking = defineEndpoint({
    ...tag,
    name: "getStorefrontTracking",
    method: "GET",
    path: "/v1/storefront-tracking",
    access: { permission: "storefront.view" },
    summary: "The shop's Pixel, Conversions API and Google Analytics settings",
    status: 200,
    response: storefrontTrackingSchema,
});
export const saveStorefrontTracking = defineEndpoint({
    ...tag,
    name: "saveStorefrontTracking",
    method: "PUT",
    path: "/v1/storefront-tracking",
    access: { permission: "storefront.manage" },
    summary: "Save the Pixel, Conversions API token and Google Analytics ID",
    body: saveStorefrontTrackingSchema,
    status: 200,
    response: storefrontTrackingSchema,
    errors: ["CONFLICT"],
});
export const shopTrackingSchema = z.object({ pixelId: z.string().nullable(), ga4Id: z.string().nullable() });
export const getShopTracking = defineEndpoint({
    ...tag,
    name: "getShopTracking",
    method: "GET",
    path: "/v1/storefront/tracking",
    access: { public: true },
    summary: "The IDs the shop's pages load the Pixel and Google Analytics with",
    query: z.object({ host: storefrontHostSchema }),
    status: 200,
    response: shopTrackingSchema,
});
/**
 * What the shopper's browser adds to an order for the Conversions API (the storefront server reads the Meta cookies
 * and the user agent). Never required; never shown back.
 */
export const shopperSignalsSchema = z
    .object({
    fbp: z.string().max(200).optional(),
    fbc: z.string().max(500).optional(),
    userAgent: z.string().max(500).optional(),
    sourceUrl: z.string().max(500).optional(),
})
    .strict();
//# sourceMappingURL=storefront-tracking.js.map